import { cardPayOnlineLabel, qrPayLabel, tt } from '@agos/shared'
import type { BadgeColor } from '@agos/ui'

/**
 * The order status vocabulary, shared by every screen in this module.
 *
 * The colours are learned: amber means "in preparation" on the kitchen board,
 * the dispatch board and the archive alike, and a member of staff reads them
 * across the room without reading the word. They are ported unchanged.
 */

// §9.149 — the labels now live in `@agos/shared`; two modules draw them and only one owned them.
export { ORDER_STATUS_LABELS, orderStatusLabel } from '@agos/shared'


export const ORDER_STATUS_BADGE_COLORS: Record<string, BadgeColor> = {
  pending: 'amber',
  new: 'blue',
  confirmed: 'cyan',
  preparing: 'amber',
  quality_check: 'cyan',
  ready: 'orange',
  on_the_way: 'violet',
  at_address: 'orange',
  delivered: 'emerald',
  cancelled: 'red',
}

export function orderStatusColor(status: string | undefined): BadgeColor {
  if (!status) return 'zinc'
  return ORDER_STATUS_BADGE_COLORS[status] ?? 'zinc'
}

/** An order that is neither delivered nor cancelled is still being worked. */
export const CLOSED_ORDER_STATUSES = ['delivered', 'cancelled']

export function isOrderClosed(status: string | undefined): boolean {
  return CLOSED_ORDER_STATUSES.includes(status ?? '')
}

/**
 * The three channels legacy hardcodes. BACKEND.md §10A.1 replaced them with
 * franchise-managed `sales_channels`, and §9.5 replaces this map with a channel
 * lookup — after parity. `bar` is labelled *Osobný odber* here because that is
 * what the screens say, even though the backend calls the channel BAR.
 */
export const ORDER_TYPE_LABELS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  bar: tt('Osobný odber'),
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: tt('Hotovosť'),
  card_terminal: tt('Karta terminál'),
  card_courier: tt('Karta kuriér'),
  // §9.767, owner O129 - the online card's one name; it said *Online* here.
  card_online: cardPayOnlineLabel(),
  // §9.741 - a door QR payment, which §10A.390 files as `qr_pay`. It fell back to the online card's
  // word below: right that the money is in the bank, wrong about how it got there.
  qr_pay: qrPayLabel(),
  card: tt('Karta'),
}

export function paymentMethodLabel(method: string | undefined): string {
  if (!method) return PAYMENT_METHOD_LABELS.card_online ?? ''
  return PAYMENT_METHOD_LABELS[method] ?? PAYMENT_METHOD_LABELS.card_online ?? ''
}

/**
 * What the main administrator may set by hand on an archive row - FRONTEND.md §9.772, owner O132.
 *
 * **The owner** (Sereď): *"zmeniť spôsob platby mi ta chyba qrpay a je tam toľko kariet že neviem ktorá je Ktorá"*.
 * The select listed every key of `PAYMENT_METHOD_LABELS` but `qr_pay`: four cards, one of them legacy's generic
 * `card`, which no order in either franchise carries. Five choices now, in the order a till thinks about money,
 * and QR Pay among them - the bank files a paid QR Pay itself since BACKEND.md §10A.424, so this is the correction
 * for the case it cannot see (a SEPA code paid in the customer's own banking app). `card` keeps its label above, so
 * an old row still reads *Karta*.
 */
export const ARCHIVE_PAYMENT_CHOICES = ['cash', 'card_terminal', 'card_courier', 'card_online', 'qr_pay'] as const

/** A choice's words in the select: the method's name and which card it is. */
export function paymentChoiceLabel(method: string): string {
  switch (method) {
    case 'card_terminal':
      return `${tt('Karta')} - ${tt('terminál v prevádzke')}`
    case 'card_courier':
      return `${tt('Karta kuriér')} - ${tt('terminál rozvozára')}`
    case 'card_online':
      return `${cardPayOnlineLabel()} - ${tt('zaplatené na webe')}`
    case 'qr_pay':
      return `${qrPayLabel()} - ${tt('zaplatené QR kódom')}`
    default:
      return paymentMethodLabel(method)
  }
}

/**
 * The sentence under the select for the chosen method - §9.772.
 *
 * **The two bank-settled ones say the money must really be in the bank**, because every handover then takes the
 * order off the cash with nothing typed (BACKEND.md §10A.402) - which is right when the bank has it and is a driver
 * short when it has not.
 */
export function paymentChoiceNote(method: string): string {
  switch (method) {
    case 'cash':
      return tt('Zaplatené v hotovosti - pri odovzdaní tržby sa počíta do hotovosti.')
    case 'card_terminal':
      return tt('Zaplatené kartou na termináli v prevádzke.')
    case 'card_courier':
      return tt('Zaplatené kartou na termináli, ktorý má rozvozár so sebou.')
    case 'card_online':
      return tt(
        'Zaplatené kartou na webe. Zvoľte, len ak peniaze naozaj prišli do banky - pri odovzdaní tržby sa nepočítajú do hotovosti.',
      )
    case 'qr_pay':
      return tt(
        'Zaplatené QR kódom do banky. Zvoľte, len ak peniaze naozaj prišli - pri odovzdaní tržby sa nepočítajú do hotovosti.',
      )
    default:
      return ''
  }
}
